
import React, { createContext, useContext, useCallback } from 'react';
import { toast } from 'sonner';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

type NotificationContextType = {
  showNotification: (type: NotificationType, title: string, message: string, duration?: number) => void;
  handleApiError: (error: unknown, fallbackMessage?: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

type NotificationProviderProps = {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const showNotification = useCallback((
    type: NotificationType, 
    title: string, 
    message: string, 
    duration?: number
  ) => {
    const toastOptions = {
      duration: duration || 5000,
      description: message,
    };

    switch (type) {
      case 'success':
        toast.success(title, toastOptions);
        break;
      case 'error':
        toast.error(title, toastOptions);
        break;
      case 'warning':
        toast.warning(title, toastOptions);
        break;
      case 'info':
        toast.info(title, toastOptions);
        break;
      default:
        toast(title, toastOptions);
    }
  }, []);

  const handleApiError = useCallback((error: unknown, fallbackMessage = 'Произошла ошибка') => {
    let errorMessage = fallbackMessage;
    let errorTitle = 'Ошибка';

    const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
    const errorObj = isRecord(error) ? error : undefined;
    
    // Handle different error types
    if (errorObj) {
      if (typeof errorObj.message === 'string') {
        errorMessage = errorObj.message;
      } else if (typeof (errorObj as { error_description?: unknown }).error_description === 'string') {
        errorMessage = (errorObj as { error_description: string }).error_description;
      } else if (typeof (errorObj as { details?: unknown }).details === 'string') {
        errorMessage = (errorObj as { details: string }).details;
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // Handle specific Supabase error codes
    if (errorObj && typeof (errorObj as { code?: unknown }).code === 'string') {
      const code = (errorObj as { code: string }).code;
      switch (code) {
        case 'PGRST116':
          errorTitle = 'Нет данных';
          errorMessage = 'Запрашиваемые данные не найдены';
          break;
        case 'PGRST301':
          errorTitle = 'Ошибка доступа';
          errorMessage = 'У вас нет прав для выполнения этого действия';
          break;
        case '23505':
          errorTitle = 'Дублирование данных';
          errorMessage = 'Такая запись уже существует';
          break;
        case '23503':
          errorTitle = 'Ошибка связи';
          errorMessage = 'Невозможно удалить запись, так как она используется в других местах';
          break;
        case 'auth/invalid-email':
          errorTitle = 'Неверный email';
          errorMessage = 'Пожалуйста, проверьте правильность email адреса';
          break;
        case 'auth/user-not-found':
          errorTitle = 'Пользователь не найден';
          errorMessage = 'Пользователь с таким email не зарегистрирован';
          break;
        case 'auth/wrong-password':
          errorTitle = 'Неверный пароль';
          errorMessage = 'Пожалуйста, проверьте правильность пароля';
          break;
        case 'auth/too-many-requests':
          errorTitle = 'Слишком много попыток';
          errorMessage = 'Превышено количество попыток. Попробуйте позже';
          break;
        default:
          if (code.startsWith('PGRST')) {
            errorTitle = 'Ошибка базы данных';
          }
      }
    }

    // Handle network errors
    if (
      (errorObj && (errorObj as { name?: unknown }).name === 'NetworkError') ||
      (errorObj && typeof (errorObj as { message?: unknown }).message === 'string' && (errorObj as { message: string }).message.includes('fetch'))
    ) {
      errorTitle = 'Ошибка сети';
      errorMessage = 'Проверьте подключение к интернету';
    }

    showNotification('error', errorTitle, errorMessage);
  }, [showNotification]);

  const clearNotifications = useCallback(() => {
    toast.dismiss();
  }, []);

  return (
    <NotificationContext.Provider value={{
      showNotification,
      handleApiError,
      clearNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
